import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Settings2, CalendarDays, LayoutGrid, BookOpen } from "lucide-react";
import { encodeData } from "../lib/cycle";
import CycleCalendar from "./CycleCalendar";
import PhaseRecommendations from "./PhaseRecommendations";
import MonthlySummary from "./MonthlySummary";
import ShareExport from "./ShareExport";
import Library from "./Library";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../i18n";

export default function Dashboard({ data, onEdit }) {
  const { t } = useI18n();
  const shareBase = (process.env.REACT_APP_SHARE_BASE_URL || window.location.origin).replace(/\/+$/, "");
  const shareUrl = `${shareBase}/?d=${encodeData(data)}`;

  return (
    <div className="min-h-screen w-full grain-bg">
      <div className="relative z-10 mx-auto max-w-md min-h-screen bg-background border-x border-border">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b border-border px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="font-serif text-xl leading-none">Cycle IQ</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button
              data-testid="edit-data-btn"
              onClick={onEdit}
              variant="outline"
              className="h-10 w-10 p-0 rounded-full border-border bg-white hover:bg-secondary active:scale-95 transition-transform"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
            <ShareExport shareUrl={shareUrl} data={data} />
          </div>
        </header>

        <div className="px-4 pb-28 pt-5">
          <Tabs defaultValue="today" className="w-full">
            <TabsList
              data-testid="dashboard-tabs"
              className="grid w-full grid-cols-3 h-12 rounded-full bg-secondary p-1 mb-5"
            >
              <TabsTrigger
                data-testid="tab-today"
                value="today"
                className="rounded-full data-[state=active]:bg-bone data-[state=active]:shadow-sm text-sm font-semibold gap-1.5"
              >
                <CalendarDays className="h-4 w-4" />
                {t("tabToday")}
              </TabsTrigger>
              <TabsTrigger
                data-testid="tab-summary"
                value="summary"
                className="rounded-full data-[state=active]:bg-bone data-[state=active]:shadow-sm text-sm font-semibold gap-1.5"
              >
                <LayoutGrid className="h-4 w-4" />
                {t("tabSummary")}
              </TabsTrigger>
              <TabsTrigger
                data-testid="tab-library"
                value="library"
                className="rounded-full data-[state=active]:bg-bone data-[state=active]:shadow-sm text-sm font-semibold gap-1.5"
              >
                <BookOpen className="h-4 w-4" />
                {t("tabLibrary")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="today" className="space-y-4 mt-0">
              <PhaseRecommendations data={data} calendar={<CycleCalendar data={data} />} />
            </TabsContent>

            <TabsContent value="summary" className="mt-0">
              <MonthlySummary data={data} />
            </TabsContent>

            <TabsContent value="library" className="mt-0">
              <Library />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
